import React from 'react';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { DataGrid } from 'v3/Components/DataGrid/DataGrid';
import { DownloadReportButton } from 'v3/Components/DataGrid/CustomCells';
import { BooleanSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/BooleanSelectFilter';
import { DateRangePickerFilterFilter } from 'v3/Components/DataGrid/ColumnFilters/DateRangePickerFilter';
import { MultiSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/MultiSelectFilter';
import { SingleSelectFilter } from 'v3/Components/DataGrid/ColumnFilters/SingleSelectFilter';
import { TextFieldFilter } from 'v3/Components/DataGrid/ColumnFilters/TextFieldFilter';
import { StatusPill } from 'v3/Components/Shared/StatusPill';
import { ENDPOINTS } from 'Models/Templates/Routes/Routes';
import { buildEndpoint, buildRoute, defaultDataTransform, useDataFetcher, formatShortDate, formatShortDateTime } from 'Utilities';
import { useEnums, useGlobalContext } from 'Context';
import { useHistory } from 'react-router-dom';
import { AddEditSubRoute, ROUTES } from 'Navigation';
import ClientSelector from 'v3/Components/Shared/ClientSelector';
import useClientSearchParams from 'v3/Utilities/useClientSearchParams';
import NewReferralButton from 'v3/Components/Shared/NewReferralButton';
import './AllCases.scss';

interface DropdownOptions {
  clients: { label: string; value: string }[];
  reviewers: { label: string; value: string }[];
  owners: { label: string; value: string }[];
  states: { label: string; value: string }[];
  specialties: { label: string; value: string }[];
  apContactPreferences: { label: string; value: string }[];
  tatPriorities: { label: string; value: string }[];
  tats: { label: string; value: string }[];
  lobs: { label: string; value: string }[];
  services: { label: string; value: string }[];
  subServices: { label: string; value: string }[];
  proxiedByUsers: string[];
  appointmentStatuses: { label: string; value: string }[];
}

const CLIENT_STATUS_OPTIONS = [
  { label: 'Under Review', value: 'Under Review' },
  { label: 'Cancelled', value: 'Cancelled' },
  { label: 'Closed', value: 'Closed' },
];

const mapClientStatus = (status: string): string => {
  if (status === 'Cancelled') return 'Cancelled';
  if (status === 'Closed') return 'Closed';
  return 'Under Review';
};

const renderSpecialtyCell = (params: any) => {
  const specialties: { displayName: string }[] = params.row.specialty ?? [];
  if (specialties.length === 0) return null;
  return (
    <span className="specialty-cell">
      <span>{specialties[0]?.displayName}</span>
      {specialties.length > 1 && (
        <span className="specialty-cell__extra">+{specialties.length - 1}</span>
      )}
    </span>
  );
};

const buildColumns = (opts: DropdownOptions, userdata: any): any[] => {
  const isInternal = userdata?.isInternal ?? false;
  const isClient = userdata?.isClient ?? false;
  const isReviewer = userdata?.isReviewer ?? false;
  const isAccountsManager = userdata?.isAccountsManager ?? false;
  const isMedicalDirector = userdata?.isMedicalDirector ?? false;
  const showDownloadIcon = !(isInternal || isAccountsManager || isMedicalDirector);
  const reportEndpoint = `${ENDPOINTS.appointmentreports.base}/ClosedReferral`;

  const columns: Array<{ _show?: boolean; [key: string]: any }> = [
    {
      field: 'stradixNumber',
      headerName: 'Stradix #',
      width: 188,
      hideable: false,
      pinnable: false,
      cellClassName: 'all-cases-cell-bold',
      renderCell: (params: any) => (
        <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          <span>{params.value}</span>
          {showDownloadIcon && params.row.appointmentStatus === 'Closed' && params.row.referralId && (
            <DownloadReportButton
              fileControllerEndpoint={reportEndpoint}
              id={params.row.referralId}
              filename={`${params.row.stradixNumber ?? ''}_report`}
            />
          )}
        </span>
      ),
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'client',
      headerName: 'Client',
      width: 200,
      type: 'singleSelect',
      _show: isInternal,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.clients} />,
    },
    {
      field: 'referralDate',
      headerName: 'Referral Date',
      width: 200,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'closedDate',
      headerName: 'Date Completed',
      width: 200,
      widthOnFilter: 260,
      renderHeader: () => (
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, overflow: 'hidden' }}>
          <span title="This is the initial date of completion" style={{ display: 'inline-flex', pointerEvents: 'auto', cursor: 'help' }} onClick={(e) => e.stopPropagation()}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary', flexShrink: 0 }} />
          </span>
          <span className="MuiDataGrid-columnHeaderTitle">Date Completed</span>
        </span>
      ),
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'dueDate',
      headerName: 'Client Due Date',
      width: 200,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'claimant',
      headerName: 'Claimant',
      width: 200,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimNumber',
      headerName: 'Claim/Client #',
      width: 160,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'owner',
      headerName: 'Owner',
      width: 180,
      _show: !isClient,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'intakeOwnerName',
      headerName: 'Intake Owner',
      width: 180,
      _show: !isClient,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'assigningOwnerName',
      headerName: 'Assignment Owner',
      width: 180,
      _show: !isClient,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'qaOwnerName',
      headerName: 'QA Owner',
      width: 180,
      _show: !isClient,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'recentUser',
      headerName: 'Recent User',
      width: 180,
      _show: isInternal,
      sortable: false,
      filterable: false,
    },
    {
      field: 'subService',
      headerName: 'Sub Service',
      width: 220,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.subServices} />,
    },
    {
      field: 'service',
      headerName: 'Service',
      width: 200,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.services} />,
    },
    {
      field: 'lob',
      headerName: 'LOB',
      width: 260,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.lobs} />,
    },
    {
      field: 'benefitState',
      headerName: 'State',
      width: 140,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.states} />,
    },
    {
      field: 'appointmentStatus',
      headerName: 'Status',
      width: 180,
      type: 'singleSelect',
      renderCell: (params: any) => {
        if (!params.value) return null;
        const display = isClient ? mapClientStatus(params.value) : params.value;
        return <StatusPill status={display} />;
      },
      renderHeaderFilter: (params: any) => (
        <SingleSelectFilter
          {...params}
          optionItems={isClient ? CLIENT_STATUS_OPTIONS : opts.appointmentStatuses}
        />
      ),
    },
    {
      field: 'reviewer',
      headerName: 'Reviewer',
      width: 180,
      type: 'singleSelect',
      _show: isInternal,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.reviewers} />,
    },
    {
      field: 'specialty',
      headerName: 'Specialty',
      width: 200,
      sortable: false,
      type: 'singleSelect',
      renderCell: renderSpecialtyCell,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.specialties} />,
    },
    {
      field: 'subspecialty',
      headerName: 'Subspecialty',
      width: 240,
      sortable: false,
      filterable: false,
    },
    {
      field: 'outcome',
      headerName: 'Outcome',
      width: 180,
      sortable: false,
      filterable: false,
    },
    {
      field: 'tat',
      headerName: 'TAT',
      width: 140,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tats} />,
    },
    {
      field: 'tatPriority',
      headerName: 'TAT Priority',
      width: 140,
      type: 'singleSelect',
      renderCell: (params: any) =>
        params.value ? <StatusPill status={params.value} /> : null,
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.tatPriorities} />,
    },
    {
      field: 'pageCount',
      headerName: 'Records Pg. #',
      width: 140,
      type: 'number',
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} inputType="number" />,
    },
    {
      field: 'apContactPreference',
      headerName: 'AP Contact Pref.',
      width: 160,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <SingleSelectFilter {...params} optionItems={opts.apContactPreferences} />,
    },
    {
      field: 'callAttempts',
      headerName: '# of Call Attempts',
      width: 160,
      type: 'number',
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} inputType="number" />,
    },
    {
      field: 'numberOfRequests',
      headerName: '# of Requests',
      width: 160,
      type: 'number',
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} inputType="number" />,
    },
    {
      field: 'referralSource',
      headerName: 'Referrer',
      width: 180,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimantStreetAddress',
      headerName: 'Claimant Street Address',
      width: 400,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimantCity',
      headerName: 'Claimant City',
      width: 260,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimantState',
      headerName: 'Claimant State',
      width: 140,
      type: 'singleSelect',
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.states} />,
    },
    {
      field: 'claimantZipCode',
      headerName: 'Claimant Zip Code',
      width: 140,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimantEmail',
      headerName: 'Claimant Email',
      width: 200,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'claimantPhone',
      headerName: 'Claimant Phone #',
      width: 160,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'dateOfExam',
      headerName: 'Date of Exam',
      width: 200,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'appointmentDate',
      headerName: 'Appointment Date',
      width: 200,
      widthOnFilter: 260,
      _show: !isReviewer,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'dateOfInjury',
      headerName: 'DOI/DOD',
      width: 200,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDate(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} dateOnly />,
    },
    {
      field: 'requestReceivedDate',
      headerName: 'Request Received',
      width: 220,
      widthOnFilter: 260,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'jmRequired',
      headerName: 'JM Required',
      width: 140,
      renderCell: (params: any) => {
        if (params.value === true) return <StatusPill status="Yes" />;
        if (params.value === false) return <StatusPill status="No" />;
        return null;
      },
      renderHeaderFilter: (params: any) => <BooleanSelectFilter {...params} />,
    },
    {
      field: 'employerName',
      headerName: 'Employer Name',
      width: 200,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'proxiedBy',
      headerName: 'Proxied By',
      width: 180,
      type: 'singleSelect',
      _show: isInternal,
      renderHeaderFilter: (params: any) => <MultiSelectFilter {...params} optionItems={opts.proxiedByUsers} />,
    },
    {
      field: 'referralNumber',
      headerName: 'Referral #',
      width: 160,
      renderHeaderFilter: (params: any) => <TextFieldFilter {...params} />,
    },
    {
      field: 'reopenedDate',
      headerName: 'Re-Opened Date',
      width: 200,
      widthOnFilter: 260,
      _show: isInternal,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'reportDueDate',
      headerName: 'Reviewer Due Date',
      width: 200,
      widthOnFilter: 260,
      _show: isInternal,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
    {
      field: 'reportDateCompleted',
      headerName: 'Reviewer Submitted Date',
      width: 220,
      widthOnFilter: 260,
      _show: isInternal,
      valueFormatter: (value: string) => formatShortDateTime(value),
      renderHeaderFilter: (params: any) => <DateRangePickerFilterFilter {...params} />,
    },
  ];

  return columns
    .filter(({ _show }) => _show === undefined || _show)
    .map(({ _show, ...col }) => col);
};

const useAllCasesDropdownOptions = (): DropdownOptions => {
  const enums: any = useEnums();

  const filterOptionsEndpoint = ENDPOINTS.allcases.endpointString('FilterOptions');

  const [clientsList] = useDataFetcher<{ id: string; name: string }[]>(
    buildEndpoint(ENDPOINTS.clients.dropdownItems),
    [],
    defaultDataTransform,
  );
  const [filterOptions] = useDataFetcher<{
    owners: { id: string; name: string }[];
    reviewers: { id: string; name: string }[];
    services: { id: string; name: string }[];
    subServices: { id: string; name: string }[];
    proxiedByUsers: string[];
  }>(
    buildEndpoint(filterOptionsEndpoint),
    { owners: [], reviewers: [], services: [], subServices: [], proxiedByUsers: [] },
    defaultDataTransform,
    undefined,
    true,
    '3.0',
  );

  return {
    clients: (clientsList ?? []).map((c) => ({ label: c.name, value: c.id })).filter((c) => c.label && c.value),
    reviewers: (filterOptions?.reviewers ?? []).map((r) => ({ label: r.name, value: r.id })),
    owners: (filterOptions?.owners ?? []).map((o) => ({ label: o.name, value: o.id })),
    states: (enums?.state ? Object.values(enums.state) : []).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    specialties: Object.values(enums?.specialty ?? {}).map((e: any) => ({
      label: e.displayName ?? '',
      value: String(e.value ?? ''),
    })),
    apContactPreferences: Object.values(enums?.apContact ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    tatPriorities: Object.values(enums?.tatPriority ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    tats: Object.values(enums?.tat ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    lobs: Object.values(enums?.lineOfBusiness ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
    services: (filterOptions?.services ?? []).map((s) => ({ label: s.name, value: s.id })),
    subServices: (filterOptions?.subServices ?? []).map((s) => ({ label: s.name, value: s.id })),
    proxiedByUsers: filterOptions?.proxiedByUsers ?? [],
    appointmentStatuses: Object.values(enums?.appointmentStatus ?? {}).map((e: any) => ({ label: e.displayName ?? '', value: String(e.value ?? '') })),
  };
};

export const AllCases: React.FC = () => {
  const history = useHistory();
  const globalContext = useGlobalContext();
  const userdata = globalContext[0]?.currentUser;
  const isClient = userdata?.isClient ?? false;
  const isInternal = userdata?.isInternal ?? false;
  const readEndpoint = buildEndpoint(ENDPOINTS.allcases.read);
  const exportEndpoint = buildEndpoint(ENDPOINTS.allcases.endpointString('ExportToCsv'));
  const dropdownOptions = useAllCasesDropdownOptions();
  const columns = React.useMemo(() => buildColumns(dropdownOptions, userdata), [dropdownOptions, userdata]);

  const legacyParams = React.useMemo(() => ({
    readEndpoint,
    apiVersion: '3.0',
  }), [readEndpoint]);

  const { setSelectedClient, clientSearchParams } = useClientSearchParams();

  const handleRowClick = (params: any) => {
    const referralId = params.row?.referralId;
    if (referralId) {
      history.push(buildRoute(AddEditSubRoute.Referral, referralId, ROUTES.VIEW));
    }
  };

  return (
    <div className='allCases'>
      <div className="main-grid-header">
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <h1>{isClient ? 'All Referrals' : 'All Cases'}</h1>
          <div className="grids-header-client-selector">
            <ClientSelector containerClassName="client-selector--large" onClientChange={setSelectedClient} />
          </div>
        </div>
        <NewReferralButton text={isClient ? 'Add Referral' : 'Add Case'} startIcon={<AddIcon />} />
      </div>
      <div className="reviewers-content">
        <DataGrid
          onRowClick={handleRowClick}
          title={isClient ? 'Referrals' : 'Cases'}
          gridId='allCasesGrid'
          enableUrlSync
          legacyParams={legacyParams}
          columns={columns}
          pinnedColumns={{ left: ['stradixNumber'] }}
          additionalSearchParams={clientSearchParams}
          exportEndpoint={exportEndpoint}
          exportFileName="AllCases"
          defaultShowFilters={isInternal}
          columnVisibilityModel={{
            proxiedBy: false,
            referralNumber: false,
            reopenedDate: false,
            reportDueDate: false,
            reportDateCompleted: false,
          }}
          enableAutoRefresh
        />
      </div>
    </div>
  );
};
